/**
 * One small tile per page (#573). The state is said in words through `aria-label`
 * ("Page 3: read"); the tile itself shows only the page number.
 */

import { useHomeworkLabels } from '../../i18n/homework-labels.js';

export interface PageTileModel {
  readonly pageId: string;
  /** 1-based. */
  readonly pageNumber: number;
  readonly state: 'read' | 'reading' | 'waiting' | 'failed';
}

export function PageTiles({
  tiles,
  total,
}: {
  readonly tiles: readonly PageTileModel[];
  /** Pages the PDF has: any past the ones the server has listed so far are drawn as waiting. */
  readonly total: number;
}): React.JSX.Element {
  const t = useHomeworkLabels();
  const known = new Set(tiles.map((tile) => tile.pageNumber));
  const waiting: PageTileModel[] = [];
  for (let n = 1; n <= total; n += 1) {
    if (!known.has(n)) waiting.push({ pageId: `waiting-${String(n)}`, pageNumber: n, state: 'waiting' });
  }
  const all = [...tiles, ...waiting].sort((a, b) => a.pageNumber - b.pageNumber);
  // The board rings one page, the one being read now; the pages queued behind it look like waiting ones.
  const currentId = all.find((tile) => tile.state === 'reading')?.pageId;
  return (
    <ol className="plain-list hw-tiles" aria-label={t.pagesHeading}>
      {all.map((tile) => (
        <li
          key={tile.pageId}
          className={`hw-tile hw-tile-${tile.state}${tile.pageId === currentId ? ' hw-tile-current' : ''}`}
          aria-label={t.pageTileState(tile.pageNumber, tile.state)}
        >
          <span className="hw-tile-lines" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className="hw-tile-n" aria-hidden="true" dir="ltr">
            {tile.pageNumber}
          </span>
        </li>
      ))}
    </ol>
  );
}
